import { Box, Slide, Typography, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, { useState } from "react";
import capture, { setPersonProperties } from "../../capture";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { hostname, uniqueId } from "../../utils";
import { createCompany } from "../../utils/companyCreation";
import { useEmployeeTerminology } from "../../utils/terminology";
import ChatApi from "../chat/chatApi";
import { EmployeePersona } from "../../types";
import CompanyBasicsStep from "./steps/CompanyBasicsStep";
import CompanyDetailsStep from "./steps/CompanyDetailsStep";
import CompanyFinalizeStep from "./steps/CompanyFinalizeStep";
import CompletionStep from "./steps/CompletionStep";
import ReferralStep from "./steps/ReferralStep";
import TeamSelectionStep from "./steps/TeamSelectionStep";
import UserInfoStep from "./steps/UserInfoStep";
import WelcomeStep from "./steps/WelcomeStep";

const OnboardingContainer = styled(Box)(({ theme }) => ({
  height: "100vh",
  width: "100vw",
  display: "flex",
  overflow: "hidden",
}));

const ContentSection = styled(Box)(({ theme }) => ({
  width: "60%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: theme.spacing(8),
  paddingLeft: theme.spacing(12),
  backgroundColor: theme.palette.background.paper,
  overflowY: "auto",
  overflowX: "hidden",
  // Hide scrollbar for Chrome, Safari and Opera
  "&::-webkit-scrollbar": {
    display: "none",
  },
  // Hide scrollbar for IE, Edge and Firefox
  msOverflowStyle: "none", // IE and Edge
  scrollbarWidth: "none", // Firefox
  [theme.breakpoints.down("md")]: {
    width: "100%",
    padding: theme.spacing(4),
    paddingLeft: theme.spacing(4),
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  width: "40%",
  height: "100%",
  position: "relative",
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.grey[900]
      : theme.palette.grey[100],
  backgroundImage:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${theme.palette.primary.dark}20 0%, ${theme.palette.secondary.dark}20 100%)`
      : `linear-gradient(135deg, ${theme.palette.primary.light}30 0%, ${theme.palette.secondary.light}30 100%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const StepContent = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 500,
}));

const WideStepContent = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 900,
}));

interface OnboardingProps {
  currentUser: any;
  getToken: () => Promise<string>;
  onComplete: (companyId: string) => void;
}

export default function Onboarding({
  currentUser,
  getToken,
  onComplete,
}: OnboardingProps) {
  const { employeesLower } = useEmployeeTerminology();
  const [activeStep, setActiveStep] = useState(0);

  // Determine if we need to collect user info (do this once and cache it)
  const [needsUserInfo] = useState(() => {
    const hasDisplayName = currentUser?.displayName && currentUser.displayName.trim().length > 0;
    return !hasDisplayName;
  });

  // Capture onboarding start on mount
  React.useEffect(() => {
    capture("Onboarding - Started", {
      needsUserInfo: needsUserInfo,
    });
  }, []);
  
  const [formData, setFormData] = useState({
    userName: currentUser?.displayName || "",
    referralSource: "",
    companyName: "",
    industry: "",
    website: "",
    size: "",
    location: "",
    description: "",
    logoUrl: "",
    selectedPlan: "free",
  });
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "left"
  );
  const [createdCompanyId, setCreatedCompanyId] = useState<string>("");
  const [selectedPersonas, setSelectedPersonas] = useState<EmployeePersona[]>([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { whitelabelSettings, whitelabelLogos } = useWhitelabel();

  const chatApi = new ChatApi();
  chatApi.init(() => {}, getToken);

  // Get app name from whitelabel settings
  const appName = whitelabelSettings?.title || "GTM AI";

  // Check if team selection is enabled
  // - Main site (no whitelabel): default true
  // - Whitelabel instances: use their setting (existing whitelabels default to false, new ones default to true)
  const showTeamSelection = whitelabelSettings
    ? (whitelabelSettings.onboardingTeamSelection ?? false)  // Whitelabel: use their setting, default false for safety
    : true;  // Main site: always show team selection

  // Check if whitelabel has default public employees configured
  const hasDefaultPublicEmployees = (whitelabelSettings?.defaultPublicEmployeeIds?.length ?? 0) > 0;

  const handleNext = () => {
    setSlideDirection("left");
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setSlideDirection("right");
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const updateFormData = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompanyBasicsNext = () => {
    // Capture company basics in PostHog
    capture("Onboarding - Company Basics", {
      companyName: formData.companyName,
      industry: formData.industry,
      website: formData.website,
    });

    // Set person properties to persist company basics
    setPersonProperties({
      companyName: formData.companyName,
      industry: formData.industry,
      website: formData.website,
    });

    handleNext();
  };

  const handleCompanyDetailsNext = () => {
    // Capture company details in PostHog
    capture("Onboarding - Company Details", {
      companySize: formData.size,
      location: formData.location,
      hasLogo: !!formData.logoUrl,
    });

    // Set person properties to persist company details
    setPersonProperties({
      companySize: formData.size,
      location: formData.location,
      hasLogo: !!formData.logoUrl,
    });

    handleNext();
  };

  const handleReferralSubmit = async (referralSource: string) => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/usage/referral-source`,
        {
          referral_source: referralSource,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      updateFormData("referralSource", referralSource);

      // Capture referral source in PostHog
      capture("Onboarding - Referral Source", {
        referralSource: referralSource,
      });

      // Set person property to persist referral source
      setPersonProperties({
        referralSource: referralSource,
      });

      handleNext();
    } catch (error) {
      console.error("Error saving referral source:", error);
      // Continue anyway - don't block onboarding
      updateFormData("referralSource", referralSource);

      // Still capture even if backend fails
      capture("Onboarding - Referral Source", {
        referralSource: referralSource,
      });

      // Set person property to persist referral source
      setPersonProperties({
        referralSource: referralSource,
      });

      handleNext();
    }
  };

  const handleUserInfoSubmit = async () => {
    try {
      const token = await getToken();
      // Update user profile in backend
      await axios.post(
        `${hostname}/users/profile`,
        {
          displayName: formData.userName,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Capture user name in PostHog
      capture("Onboarding - User Info", {
        userName: formData.userName,
      });

      // Set person property to persist user name
      setPersonProperties({
        userName: formData.userName,
      });

      handleNext();
    } catch (error) {
      console.error("Error updating user profile:", error);

      // Still capture even if backend fails
      capture("Onboarding - User Info", {
        userName: formData.userName,
      });

      // Set person property to persist user name
      setPersonProperties({
        userName: formData.userName,
      });

      // Continue anyway - don't block onboarding
      handleNext();
    }
  };

  const saveCompany = async (companyData: any) => {
    const token = await getToken();
    await axios.post(`${hostname}/companies`, companyData, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const sendWelcomeEmail = async (
    companyName: string,
    selectedPlan: string,
    userName?: string
  ) => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/users/welcome-email`,
        { companyName, selectedPlan, userName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Failed to send welcome email:", error);
      // Don't block onboarding if email fails
    }
  };

  const handleCompanySubmit = async (retry: number = 0) => {
    setLoading(true);
    setStatusText("Creating your company...");

    const companyData = {
      uid: "", // UID will be set by backend from JWT token
      id: uniqueId(),
      name: formData.companyName,
      industry: formData.industry,
      website: formData.website,
      size: formData.size,
      location: formData.location,
      description: formData.description,
      logoUrl: formData.logoUrl || "",
      type: formData.industry, // Add type field like in CompanyCreateModal
      employees: [],
    };

    // Use the userName from form if user entered it, otherwise use existing displayName
    const userWithUpdatedName = {
      ...currentUser,
      displayName: formData.userName || currentUser?.displayName || "",
    };

    try {
      // Use shared company creation logic
      // Priority for employee creation:
      // 1. If team selection is enabled, use selected personas
      // 2. If whitelabel has default public employees, use those
      // 3. Otherwise, auto-generate employees
      const result = await createCompany({
        getToken,
        companyData,
        currentUser: userWithUpdatedName,
        chatApi,
        whitelabelSettings,
        onStatusUpdate: setStatusText,
        createEmployees: !showTeamSelection && !hasDefaultPublicEmployees, // Only auto-create if no other method is used
        employeeCount: 3,
        selectedPersonas: showTeamSelection ? selectedPersonas : undefined,
        useDefaultPublicEmployees: !showTeamSelection && hasDefaultPublicEmployees, // Use default public employees when configured
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      // Store the created company ID for navigation after completion
      setCreatedCompanyId(companyData.id);

      // Send welcome email after successful setup
      setStatusText("Sending welcome email...");
      await sendWelcomeEmail(companyData.name, formData.selectedPlan, formData.userName);

      // Capture successful onboarding completion with all collected data
      capture("Onboarding - Completed", {
        companyName: formData.companyName,
        industry: formData.industry,
        website: formData.website,
        companySize: formData.size,
        location: formData.location,
        hasLogo: !!formData.logoUrl,
        referralSource: formData.referralSource,
        selectedPlan: formData.selectedPlan,
      });

      // Set all person properties at once for completed onboarding
      setPersonProperties({
        companyName: formData.companyName,
        industry: formData.industry,
        website: formData.website,
        companySize: formData.size,
        location: formData.location,
        hasLogo: !!formData.logoUrl,
        referralSource: formData.referralSource,
        selectedPlan: formData.selectedPlan,
        onboardingCompleted: true,
      });
    } catch (error: any) {
      console.error("Error creating company:", error);
      if (retry < 3) {
        handleCompanySubmit(retry + 1);
      } else {
        setStatusText("Error creating company. Please try again.");
        setLoading(false);
        return;
      }
    }

    setLoading(false);
    handleNext(); // Go to completion step
  };

  const renderStepContent = () => {
    if (needsUserInfo) {
      // Show UserInfoStep flow
      switch (activeStep) {
        case 0:
          return (
            <WelcomeStep
              appName={appName}
              userName={currentUser?.email}
              onNext={handleNext}
            />
          );
        case 1:
          return (
            <UserInfoStep
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleUserInfoSubmit}
              onBack={handleBack}
              appName={appName}
            />
          );
        case 2:
          return (
            <ReferralStep
              onSubmit={handleReferralSubmit}
              onBack={handleBack}
              appName={appName}
            />
          );
        case 3:
          return (
            <CompanyBasicsStep
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleCompanyBasicsNext}
              onBack={handleBack}
              appName={appName}
            />
          );
        case 4:
          return (
            <CompanyDetailsStep
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleCompanyDetailsNext}
              onBack={handleBack}
              appName={appName}
              getToken={getToken}
            />
          );
        case 5:
          return (
            <CompanyFinalizeStep
              formData={formData}
              updateFormData={updateFormData}
              onSubmit={showTeamSelection ? handleNext : handleCompanySubmit}
              onBack={handleBack}
              loading={loading && !showTeamSelection}
              appName={appName}
              statusText={!showTeamSelection ? statusText : ""}
              chatApi={chatApi}
            />
          );
        case 6:
          if (showTeamSelection) {
            return (
              <TeamSelectionStep
                selectedPersonas={selectedPersonas}
                onSelectPersonas={setSelectedPersonas}
                onNext={handleCompanySubmit}
                onBack={handleBack}
                loading={loading}
                appName={appName}
                getToken={getToken}
                minSelections={3}
              />
            );
          }
          return (
            <CompletionStep
              appName={appName}
              companyName={formData.companyName}
              selectedPlan={formData.selectedPlan}
              onComplete={() => onComplete(createdCompanyId)}
            />
          );
        case 7:
          if (showTeamSelection) {
            return (
              <CompletionStep
                appName={appName}
                companyName={formData.companyName}
                selectedPlan={formData.selectedPlan}
                onComplete={() => onComplete(createdCompanyId)}
              />
            );
          }
          return null;
        default:
          return null;
      }
    } else {
      // Skip UserInfoStep flow (user already has a name)
      switch (activeStep) {
        case 0:
          return (
            <WelcomeStep
              appName={appName}
              userName={formData.userName || currentUser?.displayName || currentUser?.email}
              onNext={handleNext}
            />
          );
        case 1:
          return (
            <ReferralStep
              onSubmit={handleReferralSubmit}
              onBack={handleBack}
              appName={appName}
            />
          );
        case 2:
          return (
            <CompanyBasicsStep
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleCompanyBasicsNext}
              onBack={handleBack}
              appName={appName}
            />
          );
        case 3:
          return (
            <CompanyDetailsStep
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleCompanyDetailsNext}
              onBack={handleBack}
              appName={appName}
              getToken={getToken}
            />
          );
        case 4:
          return (
            <CompanyFinalizeStep
              formData={formData}
              updateFormData={updateFormData}
              onSubmit={showTeamSelection ? handleNext : handleCompanySubmit}
              onBack={handleBack}
              loading={loading && !showTeamSelection}
              appName={appName}
              statusText={!showTeamSelection ? statusText : ""}
              chatApi={chatApi}
            />
          );
        case 5:
          if (showTeamSelection) {
            return (
              <TeamSelectionStep
                selectedPersonas={selectedPersonas}
                onSelectPersonas={setSelectedPersonas}
                onNext={handleCompanySubmit}
                onBack={handleBack}
                loading={loading}
                appName={appName}
                getToken={getToken}
                minSelections={3}
              />
            );
          }
          return (
            <CompletionStep
              appName={appName}
              companyName={formData.companyName}
              selectedPlan={formData.selectedPlan}
              onComplete={() => onComplete(createdCompanyId)}
            />
          );
        case 6:
          if (showTeamSelection) {
            return (
              <CompletionStep
                appName={appName}
                companyName={formData.companyName}
                selectedPlan={formData.selectedPlan}
                onComplete={() => onComplete(createdCompanyId)}
              />
            );
          }
          return null;
        default:
          return null;
      }
    }
  };

  // Determine if we're on the team selection step (needs wider container)
  const isTeamSelectionStep = showTeamSelection && (
    (needsUserInfo && activeStep === 6) || (!needsUserInfo && activeStep === 5)
  );

  const ContentWrapper = isTeamSelectionStep ? WideStepContent : StepContent;

  return (
    <OnboardingContainer>
      <ContentSection>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            py: 4,
          }}
        >
          <ContentWrapper>
            <Slide
              direction={slideDirection}
              in={true}
              mountOnEnter
              unmountOnExit
            >
              <Box>{renderStepContent()}</Box>
            </Slide>
          </ContentWrapper>
        </Box>
      </ContentSection>

      <ImageSection>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundImage: whitelabelLogos?.onboarding_hero
              ? `url(${whitelabelLogos.onboarding_hero.url})`
              : `url(${require("../../img/hero.jpg")})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            position: "relative",
          }}
        >
          {/* Overlay for better text readability */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.5)"
                  : "rgba(0,0,0,0.3)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              textAlign: "center",
              px: 2,
            }}
          >
            {/* Dynamic content based on current step */}
            {activeStep === 0 && (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.75rem", md: "2.5rem", lg: "3rem" },
                  }}
                >
                  Welcome to the Future of Work
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 600,
                    fontSize: { xs: "0.875rem", md: "1.125rem", lg: "1.25rem" },
                  }}
                >
                  Build your AI team in minutes and transform how your business
                  operates
                </Typography>
              </>
            )}

            {/* Step 1: UserInfoStep (if needed) or ReferralStep */}
            {((needsUserInfo && activeStep === 1) || (!needsUserInfo && activeStep === 1)) && (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.75rem", md: "2.5rem", lg: "3rem" },
                  }}
                >
                  {needsUserInfo ? "Tell Us About You" : "Join Thousands of Companies"}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 600,
                    fontSize: { xs: "0.875rem", md: "1.125rem", lg: "1.25rem" },
                  }}
                >
                  {needsUserInfo
                    ? "Help us personalize your experience"
                    : `Discover how businesses like yours are scaling with AI ${employeesLower}`
                  }
                </Typography>
              </>
            )}

            {/* Step 2: ReferralStep (if user info collected) or CompanyBasicsStep */}
            {((needsUserInfo && activeStep === 2) || (!needsUserInfo && activeStep === 2)) && (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.75rem", md: "2.5rem", lg: "3rem" },
                  }}
                >
                  {needsUserInfo ? "Join Thousands of Companies" : "Tell Us About Your Business"}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 600,
                    fontSize: { xs: "0.875rem", md: "1.125rem", lg: "1.25rem" },
                  }}
                >
                  {needsUserInfo
                    ? `Discover how businesses like yours are scaling with AI ${employeesLower}`
                    : "We'll customize your AI team to match your unique needs and goals"
                  }
                </Typography>
              </>
            )}

            {/* Steps 3-4 (with user info) or Steps 3 (without): Company details */}
            {((needsUserInfo && (activeStep === 3 || activeStep === 4)) || (!needsUserInfo && activeStep === 3)) && (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.75rem", md: "2.5rem", lg: "3rem" },
                  }}
                >
                  Tell Us About Your Business
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 600,
                    fontSize: { xs: "0.875rem", md: "1.125rem", lg: "1.25rem" },
                  }}
                >
                  We'll customize your AI team to match your unique needs and goals
                </Typography>
              </>
            )}

            {/* Step 5 (with user info) or Step 4 (without): Finalize */}
            {((needsUserInfo && activeStep === 5) || (!needsUserInfo && activeStep === 4)) && (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.75rem", md: "2.5rem", lg: "3rem" },
                  }}
                >
                  Finalize Your Setup
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 600,
                    fontSize: { xs: "0.875rem", md: "1.125rem", lg: "1.25rem" },
                  }}
                >
                  Review your company details and we'll create your AI team
                </Typography>
              </>
            )}

            {/* Step 6 (with user info) or Step 5 (without): Team Selection or Completion */}
            {((needsUserInfo && activeStep === 6) || (!needsUserInfo && activeStep === 5)) && (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.75rem", md: "2.5rem", lg: "3rem" },
                  }}
                >
                  {showTeamSelection ? "Build Your Dream Team" : "You're All Set! 🎉"}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 600,
                    fontSize: { xs: "0.875rem", md: "1.125rem", lg: "1.25rem" },
                  }}
                >
                  {showTeamSelection
                    ? `Choose the AI ${employeesLower} that best fit your business needs`
                    : "Your AI team is ready. Let's start building something amazing together"
                  }
                </Typography>
              </>
            )}

            {/* Step 7 (with user info) or Step 6 (without): Completion when team selection enabled */}
            {showTeamSelection && ((needsUserInfo && activeStep === 7) || (!needsUserInfo && activeStep === 6)) && (
              <>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.75rem", md: "2.5rem", lg: "3rem" },
                  }}
                >
                  You're All Set! 🎉
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    opacity: 0.9,
                    maxWidth: 600,
                    fontSize: { xs: "0.875rem", md: "1.125rem", lg: "1.25rem" },
                  }}
                >
                  Your AI team is ready. Let's start building something amazing
                  together
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </ImageSection>
    </OnboardingContainer>
  );
}
